import { useState } from "react";
import {
  Alert,
  Box,
  Divider,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Portal,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  ArrowDropDown,
  CreateNewFolderOutlined,
  DataObjectOutlined,
  PictureAsPdfOutlined,
} from "@mui/icons-material";

import { useUploadFile } from "src/api/projectFolders/projectFolderService";
import ProjectPickerDialog from "src/features/library/dialogs/ProjectPickerDialog";
import type { ProjectFolder } from "src/api/projectFolders/types";
import {
  buildProvenanceExportBlob,
  exportMessageProvenance,
} from "../../services/ProvenanceService";

type ProvenanceExportButtonProps = {
  dialogueId: number;
  messageId: string;
  /**
   * Icon-only rendering for tight footers (e.g. the per-response summary line
   * next to "View Sources"). Defaults to the labeled text button.
   */
  compact?: boolean;
};

/**
 * Export-provenance control for the Sources panel (spec Story 6).
 *
 * A split button: the primary click runs the formal PDF summary export in one
 * click — the DEFAULT export action per spec §7.1 — while the caret opens the
 * secondary menu (JSON audit trail for power users; save the PDF straight into
 * a project folder so it lands alongside the deliverable). All formats stream
 * from ``/datasets/messages/provenance/export``. The control stays disabled
 * while an export is in flight so a slow Gotenberg render can't be
 * double-triggered.
 */
const ProvenanceExportButton = ({
  dialogueId,
  messageId,
  compact = false,
}: ProvenanceExportButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const uploadFile = useUploadFile();

  const handleExport = async (
    format: "json" | "pdf",
    scope: "message" | "dialogue" = "message",
  ) => {
    setAnchorEl(null);
    setExporting(true);
    try {
      await exportMessageProvenance(dialogueId, messageId, format, scope);
    } catch (err) {
      console.error("Failed to export provenance:", err);
      setError("Couldn't export provenance. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const openPicker = () => {
    setAnchorEl(null);
    setPickerOpen(true);
  };

  const saveToFolder = async (folder: ProjectFolder) => {
    setExporting(true);
    try {
      // The formal PDF report is what belongs in a work package alongside the
      // deliverable (spec §7.1); JSON stays a download for power users.
      const { blob, filename } = await buildProvenanceExportBlob(
        dialogueId,
        messageId,
        "pdf",
      );
      const file = new File([blob], filename, {
        type: blob.type || "application/pdf",
      });
      const result = await uploadFile.mutateAsync({
        folderId: folder.id,
        file,
      });
      setPickerOpen(false);
      setSuccess(`Saved "${result?.name ?? filename}" to ${folder.name}`);
    } catch (err: unknown) {
      const detail = (err as any)?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Couldn't save the export to that folder.",
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {compact ? (
        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
          <Tooltip title="Export provenance report (PDF)" arrow>
            {/* span so the tooltip still anchors while the button is disabled */}
            <span>
              <IconButton
                size="small"
                disabled={exporting}
                onClick={() => handleExport("pdf")}
                data-testid="aa-src-export"
                data-attr="atomassist:btn:export-provenance"
                sx={{ p: 0.25 }}
              >
                {exporting ? (
                  <CircularProgress size={14} />
                ) : (
                  <PictureAsPdfOutlined sx={{ fontSize: "1rem" }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="More export options" arrow>
            <span>
              <IconButton
                size="small"
                disabled={exporting}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                data-testid="aa-src-export-more"
                sx={{ p: 0 }}
              >
                <ArrowDropDown sx={{ fontSize: "1rem" }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ) : (
        <ButtonGroup
          size="small"
          variant="text"
          disabled={exporting}
          sx={{ "& .MuiButtonGroup-grouped": { minWidth: 0 } }}
        >
          <Button
            onClick={() => handleExport("pdf")}
            startIcon={
              exporting ? (
                <CircularProgress size={14} />
              ) : (
                <PictureAsPdfOutlined sx={{ fontSize: "1rem" }} />
              )
            }
            data-testid="aa-src-export"
            data-attr="atomassist:btn:export-provenance"
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Export PDF
          </Button>
          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            data-testid="aa-src-export-more"
            aria-label="More export options"
            sx={{ px: 0.25 }}
          >
            <ArrowDropDown sx={{ fontSize: "1rem" }} />
          </Button>
        </ButtonGroup>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => handleExport("json")}
          data-testid="aa-src-export-json"
        >
          <ListItemIcon>
            <DataObjectOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>JSON audit trail</ListItemText>
        </MenuItem>
        <MenuItem onClick={openPicker} data-testid="aa-src-export-to-folder">
          <ListItemIcon>
            <CreateNewFolderOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save PDF to project folder</ListItemText>
        </MenuItem>
        <Divider />
        {/* Whole-conversation audit artifact (#20423 audit gap 5): the
            session-accumulated Searched/Read/Cited trail under the same
            dialogue-global citation numbers the user saw inline. */}
        <MenuItem
          onClick={() => handleExport("pdf", "dialogue")}
          data-testid="aa-src-export-dialogue-pdf"
        >
          <ListItemIcon>
            <PictureAsPdfOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Whole conversation (PDF)</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleExport("json", "dialogue")}
          data-testid="aa-src-export-dialogue-json"
        >
          <ListItemIcon>
            <DataObjectOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Whole conversation (JSON)</ListItemText>
        </MenuItem>
      </Menu>

      {/* Mounted only while open, so useFolders (an eager infinite query) never
          fetches on chat load — only when the user chooses to save (#20423 M5). */}
      {pickerOpen && (
        <ProjectPickerDialog
          open
          onClose={() => setPickerOpen(false)}
          title="Save provenance report to a project"
          onPick={(folder) => void saveToFolder(folder)}
          busy={exporting}
          emptyMessage="Create a writable project folder before saving this report."
          testIdPrefix="save-provenance"
          newProjectDataAttr="atomassist:btn:save-provenance-new-project"
        />
      )}

      {/* A standalone MUI Snackbar renders inline (no portal) and floats via
          `position: fixed`. This control lives inside the sources Drawer, whose
          transformed `MuiDrawer-paper` becomes the containing block for fixed
          descendants — trapping the toast low and behind the panel content.
          Portal to <body> so it escapes that stacking context and floats over
          the viewport as intended. */}
      <Portal>
        <Snackbar
          open={!!error}
          autoHideDuration={5000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default ProvenanceExportButton;
